/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { BaseComponent } from '../../../../../core/components';
import { Component } from '@angular/core';
import { ErrorResponseModel } from '../../../../../core/models';
import { LogService, ModalService, RbacService } from '../../../../../core/services';
import { SNMPConfigurationApiService } from '../../../shared/http';
import { forkJoin, Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-snmp',
    templateUrl: './snmp.component.html'
})

export class SnmpComponent extends BaseComponent {
    /**
     * SNMP disabled flag.
     */
    public snmpOff: boolean = true;

    /**
     * Store protocols in use.
     */
    public protocolsInUse: string = null;

    /**
     * Store SNMP settings.
     */
    public snmpSettings: any = {};

    /**
     * Users table rows.
     */
    public userTableRows: any[] = [];

    /**
     * Store permissions in lookup table.
     */
    public permissionsLookup: any = {};

    constructor(
        private logService: LogService,
        private modalService: ModalService,
        private rbacService: RbacService,
        private snmpConfigApiService: SNMPConfigurationApiService,
    ) {
        super();

        this.setUpPageData();
        this.registerButtons();
    }

    /**
     * Confirm delete SNMP user.
     * @param id
     */
    public confirmDeleteUser(id: string): void {
        const confirmationObject = {
            title: 'delete_user',
            message: 'snmp_conf_delete_user_message',
            callBack: this.deleteUser.bind(this, id),
            options: { translateText: true }
        };

        this.modalService.confirmDecision(confirmationObject);
    }

    /**
     * Delete user.
     * @param id
     * @private
     */
    private deleteUser(id: string): Observable<any> {
        return new Observable((observer) => {
            this.snmpConfigApiService.deleteSNMPUser(id)
                .pipe(takeUntil(this.destroyed$))
                .subscribe({
                    next: (): void => {
                        this.setUpPageData();
                        observer.next();
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Set up page data.
     * @private
     */
    private setUpPageData(): void {
        const apiCalls = {
            snmpConfig: this.snmpConfigApiService.getSNMP(),
            users: this.snmpConfigApiService.getAllSNMPUsers(),
        };
        forkJoin<any>(apiCalls)
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (dataGroups: any): void => {
                    this.setPageState(dataGroups);
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.error('SNMPConfigComponent: setUpPageData failed to fetch data ', error.message);
                }
            });
    }

    /**
     * Set the page state based on data coming from API.
     * @param data
     * @private
     */
    private setPageState(data: any): void {
        this.snmpSettings = data['snmpConfig'];
        const users = data['users'];

        this.snmpOff = !this.snmpSettings['enabledV2'] && !this.snmpSettings['enabledV3'];

        if (!this.snmpOff) {
            this.setProtocolsInUse();
        }

        if (users) {
            this.populateUsersTableRows(users);
        }
    }

    /**
     * Set protocols in use.
     * @private
     */
    private setProtocolsInUse(): void {
        if (this.snmpSettings.enabledV2 && !this.snmpSettings.enabledV3) {
            this.protocolsInUse = 'V2';
        } else if (!this.snmpSettings.enabledV2 && this.snmpSettings.enabledV3) {
            this.protocolsInUse = 'V3';
        } else if (this.snmpSettings.enabledV2 && this.snmpSettings.enabledV3) {
            this.protocolsInUse = 'V2, V3';
        }
    }

    /**
     * Populate the users table.
     * @param users
     * @private
     */
    private populateUsersTableRows(users: any): void {
        this.userTableRows = [];
        for (const user of users) {
            this.userTableRows.push({
                id: user.id,
                user: user.username,
                authentication: user.authAlgorithm,
                privacy: user.privacyAlgorithm,
                permission: user.writeAccess ? 'write' : 'read'
            });
        }
    }

    /**
     * Register the page's buttons for RBAC.
     * @private
     */
    private registerButtons(): void {
        const buttonReference = {
            'snmp-config-set': [{ path: '/snmp', methods: ['PUT'] }],
            'snmp-add-user': [{ path: '/snmp/users', methods: ['POST'] }],
            'snmp-edit-user': [{ path: '/snmp/users/:id', methods: ['PUT'] }],
            'snmp-delete-user': [{ path: '/snmp/users/:id', methods: ['DELETE'] }],
        };

        this.permissionsLookup = this.rbacService.getPermissionsLookup(buttonReference);
    }
}
